import React, { useMemo, useState } from "react";
import {
  MaterialReactTable,
  MRT_ExpandButton,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { useMIS } from "../MISContext";

const STATUS_OPTIONS = ["Open", "In Progress", "Closed"];

function toNumber(value) {
  if (value === null || value === undefined) return 0;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

export default function DeviationTable() {
  const {
    deviationRows,
    deviationLoading,
    deviationSaving,
    updateDeviationRow,
    saveDeviationsToApi,
  } = useMIS();

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await saveDeviationsToApi(deviationRows);
      setSnackbar({ open: true, severity: "success", message: "Deviation data saved successfully" });
    } catch {
      setSnackbar({ open: true, severity: "error", message: "Failed to save deviation data" });
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "category", header: "Category", enableEditing: false },

      {
        accessorKey: "referenceSource",
        header: "Reference (Source)",
        enableEditing: false,
      },
      {
        accessorKey: "budgeted",
        enableEditing: false,
        header: (
          <Box sx={{ lineHeight: 1.2 }}>
            <div>Budgeted</div>
            <div style={{ fontSize: "0.75rem", marginTop: "2px" }}>
              (hrs or $)
            </div>
          </Box>
        ),
        Cell: ({ cell, row }) => {
          if (row.getCanExpand?.()) return cell.getValue();
          const rawValue = cell.getValue();
          if (rawValue === null || rawValue === undefined || rawValue === "") {
            return "";
          }
          const category =
            (row.original?.category ?? row.getValue?.("category") ?? "").toString().toLowerCase();
          const suffix = category === "mcs" ? "$" : "hr";
          return `${rawValue} ${suffix}`;
        },
      },
      {
        accessorKey: "actual",
        enableEditing: false,
        header: (
          <Box sx={{ lineHeight: 1.2 }}>
            <div>Actual</div>
            <div style={{ fontSize: "0.75rem", marginTop: "2px" }}>
              (hrs or $)
            </div>
          </Box>
        ),
        Cell: ({ cell, row }) => {
          if (row.getCanExpand?.()) return cell.getValue();
          const rawValue = cell.getValue();
          if (rawValue === null || rawValue === undefined || rawValue === "") {
            return "";
          }
          const category =
            (row.original?.category ?? row.getValue?.("category") ?? "").toString().toLowerCase();
          const suffix = category === "mcs" ? "$" : "hr";
          return `${rawValue} ${suffix}`;
        },
      },

      {
        id: "deviation",
        header: "Deviation",
        accessorFn: (row) =>
          toNumber(row.actual) - toNumber(row.budgeted) > 0
            ? toNumber(row.actual) - toNumber(row.budgeted)
            : null,
        enableEditing: false,
        Cell: ({ cell, row }) => {
          const rawValue = cell.getValue();
          if (rawValue === null || rawValue === undefined || rawValue === "") {
            return "";
          }
          const category =
            (row.original?.category ?? row.getValue?.("category") ?? "").toString().toLowerCase();
          const suffix = category === "mcs" ? "$" : "hr";
          return `${rawValue} ${suffix}`;
        },
      },

      { accessorKey: "remarks", header: "Remarks", enableEditing: true },
      {
        accessorKey: "actionTaken",
        header: "Action Taken",
        enableEditing: true,
      },
      {
        accessorKey: "deviationStatus",
        header: "Deviation Status",
        enableEditing: true,
        editVariant: "select",
        editSelectOptions: STATUS_OPTIONS,
        muiEditTextFieldProps: { select: true },
      },
      {
        accessorKey: "responsiblePerson",
        header: "Responsible Person",
        enableEditing: false,
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: deviationRows.filter(
      (row) => toNumber(row.actual) - toNumber(row.budgeted) > 0,
    ),

    enableGrouping: true,
    groupedColumnMode: "remove",

    initialState: {
      grouping: ["category"],
      expanded: true,
    },

    enableRowActions: true,
    positionActionsColumn: "last",

    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableStickyHeader: true,

    enableEditing: true,
    editDisplayMode: "modal",

    muiEditTextFieldProps: {
      variant: "standard",
      size: "small",
      fullWidth: true,
    },

    onEditingRowSave: async ({ row, values, table }) => {
      const isGroupRow = row.getCanExpand?.();
      if (isGroupRow) {
        table.setEditingRow(null);
        return;
      }

      await updateDeviationRow(row.original.id, {
        remarks: String(values.remarks ?? ""),
        actionTaken: String(values.actionTaken ?? ""),
        deviationStatus: String(values.deviationStatus ?? ""),
      });

      table.setEditingRow(null);
    },

    renderRowActions: ({ row, table }) => {
      const isGroupRow = row.getCanExpand?.();
      if (isGroupRow) return null;

      return (
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)} size="small">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    },

    displayColumnDefOptions: {
      "mrt-row-actions": { size: 60 },
      "mrt-row-expand": {
        header: "Category",
        size: 140,
        muiTableHeadCellProps: {
          sx: {
            textAlign: "left",

            "& .Mui-TableHeadCell-Content": {
              justifyContent: "flex-start",
            },

            "& .Mui-TableHeadCell-Content-Wrapper": {
              textAlign: "left",
            },

            "& .MuiTableSortLabel-root": {
              justifyContent: "flex-start",
            },
          },
        },

        Cell: ({ row, table }) => {
          if (!row.getCanExpand?.()) return null;

          const categoryTitle =
            row.groupingColumnId === "category"
              ? row.getValue("category")
              : row.getValue(row.groupingColumnId) || row.id;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MRT_ExpandButton row={row} table={table} />
              <Typography sx={{ fontWeight: 800 }}>{categoryTitle}</Typography>
            </Box>
          );
        },
      },
    },

    muiTableBodyRowProps: ({ row }) => {
      const isGroupRow = row.getCanExpand?.();
      if (!isGroupRow) return {};
      return {
        sx: {
          backgroundColor: "rgba(0,0,0,0.06)",
          "& td": { fontWeight: 800 },
        },
      };
    },

    state: {
      isLoading: deviationLoading,
      showSaving: deviationSaving,
      showProgressBars: deviationLoading,
    },

    muiTableProps: {
      sx: { tableLayout: "fixed", borderCollapse: "collapse", width: "100%" },
    },

    muiTableHeadCellProps: {
      sx: {
        borderRight: "1px solid rgba(0,0,0,0.12)",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        padding: "4px 6px",
        fontSize: "0.95rem",
        fontWeight: 700,
        verticalAlign: "middle",
        textAlign: "center",

        "& .Mui-TableHeadCell-Content": {
          width: "100%",
          justifyContent: "center",
        },

        "& .Mui-TableHeadCell-Content-Wrapper": {
          width: "100%",
          textAlign: "center",
        },

        "& .MuiTableSortLabel-root": {
          width: "100%",
          justifyContent: "center",
        },
      },
    },

    muiTableBodyCellProps: {
      sx: {
        borderRight: "1px solid rgba(0,0,0,0.12)",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        padding: "4px 6px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        verticalAlign: "middle",
        textAlign: "center",
      },
    },

    muiTableContainerProps: { sx: { maxWidth: "100%", overflowX: "auto" } },
    enableSorting: false,
    enableColumnActions: false,
    enableColumnOrdering: false,
    enableColumnDragging: false,
    enableColumnResizing: false,
    enableFilters: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,

    defaultColumn: {
      size: 120,
      minSize: 60,
      maxSize: 220,
    },
  });

  return (
    <Box sx={{ position: "relative" }}>
      {(saving || deviationSaving) && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.7)",
            zIndex: 10,
            borderRadius: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5">Deviation Report</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveAll}
          disabled={saving || deviationLoading || deviationSaving}
        >
          {saving ? "Saving…" : "Save All"}
        </Button>
      </Box>
      <MaterialReactTable table={table} />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
